import React, { useState } from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
  SalonPublicThemeProvider,
  useSalonPublicTheme,
} from '../../../../context/SalonPublicThemeContext';
import SalonTemplateContactLocation from '../blocks/SalonTemplateContactLocation';
import SalonTemplateGallery from '../blocks/SalonTemplateGallery';
import SalonTemplateOpeningHours from '../blocks/SalonTemplateOpeningHours';
import SalonTemplateReviews from '../blocks/SalonTemplateReviews';
import SalonTemplateServices from '../blocks/SalonTemplateServices';
import SalonTemplateTeam from '../blocks/SalonTemplateTeam';
import GlossLabIdentity from './GlossLabIdentity';


const GlossLabTemplate = ({
  viewModel,
  actions,
  mode = 'public',
  viewport = 'desktop',
  direction = 'ltr',
}) => {
  const { t } = useTranslation();
  const { config, layout, tokens: theme } = useSalonPublicTheme();
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const identity = viewModel?.identity || {};
  const photos = Array.isArray(viewModel?.photos) ? viewModel.photos : [];
  const reviews = Array.isArray(viewModel?.reviews) ? viewModel.reviews : [];
  const openingHours = Array.isArray(viewModel?.openingHours) ? viewModel.openingHours : [];
  const closures = Array.isArray(viewModel?.closures) ? viewModel.closures : [];
  const categories = Array.isArray(viewModel?.serviceCategories)
    ? viewModel.serviceCategories.filter((category) => (
      Array.isArray(category?.services) && category.services.length > 0
    ))
    : [];
  const tabCategories = categories.filter((category) => (
    category?.id != null && typeof category?.name === 'string' && category.name.trim()
  ));
  const selectedCategoryId = tabCategories.some((category) => (
    String(category.id) === activeCategoryId
  )) ? activeCategoryId : tabCategories[0] ? String(tabCategories[0].id) : null;
  const visibleCategories = selectedCategoryId == null
    ? categories
    : categories.filter((category) => String(category.id) === selectedCategoryId);
  const publicState = viewModel?.publicState || {};
  const visibility = layout.tokens.visibility;
  const mobile = viewport === 'mobile';
  const hasReviews = Boolean(
    reviews.length > 0 || publicState.reviewsLoading || publicState.reviewsError,
  );
  const hasGallery = Boolean(
    visibility.gallery && (photos.length > 0 || mode === 'preview'),
  );
  const hasTeam = Boolean(
    visibility.team && viewModel?.team?.some((member) => member?.id != null),
  );
  const hasCatalog = Boolean(categories.length > 0 || publicState.servicesLoading);
  const hasPracticalInfo = Boolean(
    visibility.opening_hours && (openingHours.length > 0 || closures.length > 0),
  );
  const tileLayoutConfig = {
    ...layout.config,
    content: {
      ...layout.config.content,
      services: 'grid',
      service_columns: 3,
    },
  };

  return (
    <Box
      data-testid="salon-template-renderer"
      data-salon-template="gloss_lab"
      data-mode={mode}
      data-viewport={viewport}
      dir={direction}
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        minHeight: 0,
        minWidth: 0,
        width: '100%',
        backgroundColor: theme.salon.background,
      }}
    >
      <GlossLabIdentity
        data={{
          ...identity,
          heroPhotos: photos.slice(0, 3),
          averageRating: viewModel?.averageRating,
          reviewCount: reviews.length,
          isFavorite: viewModel?.capabilities?.isFavorite,
        }}
        mode={mode}
        onBack={actions?.onBack}
        onToggleFavorite={actions?.onToggleFavorite}
        onOpenMobileInfo={actions?.onOpenMobileInfo}
        onBook={actions?.onOpenBooking}
        onContact={actions?.onContact}
        clientChatEnabled={identity.raw?.client_chat_enabled}
      />

      <Box
        data-testid="gloss-lab-scroll-region"
        data-scroll-container="document"
        sx={{
          flex: 'none',
          minHeight: 'auto',
          width: '100%',
          overflowY: 'visible',
          overflowX: 'hidden',
        }}
      >
        <Box
          component="main"
          data-testid="gloss-lab-content"
          data-layout={mobile ? 'single-column' : 'graphic-catalog'}
          sx={{
            width: '100%',
            maxWidth: 1320,
            mx: 'auto',
            px: { xs: 2, md: 5 },
            py: { xs: 3, md: 6 },
            boxSizing: 'border-box',
          }}
        >
          {hasGallery ? (
            <Box
              data-testid="gloss-lab-lookbook"
              sx={{
                p: { xs: 2, md: 3 },
                overflow: 'hidden',
                color: theme.salon.onSecondary,
                backgroundColor: theme.salon.secondary,
                borderRadius: { xs: 3, md: 5 },
                '& [data-testid="salon-section-gallery"]': { mt: 0 },
                '& [data-testid="salon-public-gallery"]': {
                  gridTemplateColumns: {
                    xs: 'minmax(0, 1fr)',
                    sm: 'repeat(3, minmax(0, 1fr))',
                  },
                },
              }}
            >
              <SalonTemplateGallery
                data={photos}
                salonId={identity.id}
                variant="mosaic"
                visible={visibility.gallery}
                mode={mode}
                onSelectPhoto={actions?.onSelectPhoto}
                onNavigatePhoto={actions?.onNavigatePhoto}
                selectedIndex={publicState.photoIndex}
              />
            </Box>
          ) : null}

          {hasCatalog ? (
            <Box
              data-testid="gloss-lab-catalog"
              sx={{
                mt: hasGallery ? { xs: 3, md: 5 } : 0,
                p: { xs: 2, md: 4 },
                minWidth: 0,
                backgroundColor: theme.salon.surface,
                border: `1px solid ${theme.salon.border}`,
                borderRadius: { xs: 3, md: 5 },
              }}
            >
              {tabCategories.length > 0 ? (
                <Box data-salon-template-block="categories" sx={{ mb: 4, minWidth: 0 }}>
                  <Tabs
                    value={selectedCategoryId}
                    onChange={(_event, value) => setActiveCategoryId(value)}
                    aria-label={t('landing_salon_details.our_services')}
                    variant="scrollable"
                    scrollButtons="auto"
                    selectionFollowsFocus
                    allowScrollButtonsMobile
                    sx={{
                      minHeight: 0,
                      p: 0.75,
                      borderRadius: 999,
                      backgroundColor: theme.salon.secondary,
                      '& .MuiTabs-indicator': { display: 'none' },
                      '& .MuiTab-root': {
                        minHeight: 44,
                        px: 2.75,
                        color: theme.salon.onSecondary,
                        borderRadius: 999,
                        fontWeight: 800,
                        textTransform: 'none',
                      },
                      '& .MuiTab-root.Mui-selected': {
                        color: theme.salon.secondary,
                        backgroundColor: theme.salon.primary,
                      },
                    }}
                  >
                    {tabCategories.map((category) => (
                      <Tab
                        key={category.id}
                        id={`gloss-lab-tab-${category.id}`}
                        aria-controls={String(category.id) === selectedCategoryId
                          ? `gloss-lab-panel-${category.id}`
                          : undefined}
                        label={category.name}
                        value={String(category.id)}
                      />
                    ))}
                  </Tabs>
                </Box>
              ) : null}

              <SalonPublicThemeProvider
                config={config}
                layoutConfig={tileLayoutConfig}
                templateKey="gloss_lab"
                direction={direction}
              >
                <Box
                  role={selectedCategoryId == null ? undefined : 'tabpanel'}
                  id={selectedCategoryId == null ? undefined : `gloss-lab-panel-${selectedCategoryId}`}
                  aria-labelledby={selectedCategoryId == null
                    ? undefined
                    : `gloss-lab-tab-${selectedCategoryId}`}
                >
                  <SalonTemplateServices
                    data={visibleCategories}
                    variant="tiles"
                    mode={mode}
                    viewport={viewport}
                    loading={Boolean(publicState.servicesLoading)}
                    onBookService={actions?.onBookService}
                  />
                </Box>
              </SalonPublicThemeProvider>
            </Box>
          ) : null}

          {hasTeam || hasReviews ? (
            <Box
              data-testid="gloss-lab-social-grid"
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                alignItems: 'start',
                gap: { xs: 2, md: 3 },
                mt: { xs: 3, md: 5 },
                '& [data-testid="salon-section-team"]': { mt: 0 },
                '& [data-testid="salon-section-reviews"]': { mt: 0 },
              }}
            >
              {hasTeam ? (
                <SalonTemplateTeam
                  data={viewModel?.team}
                  variant="cards"
                  visible={visibility.team}
                  mode={mode}
                  onSelectMember={actions?.onSelectTeamMember}
                />
              ) : null}
              {hasReviews ? (
                <SalonTemplateReviews
                  data={{
                    items: reviews,
                    averageRating: viewModel?.averageRating,
                    isAuthenticated: Boolean(publicState.isAuthenticated),
                    isLoading: Boolean(publicState.reviewsLoading),
                    error: publicState.reviewsError || null,
                  }}
                  variant="compact"
                  visible={visibility.reviews}
                  mode={mode}
                  onOpenReviewComposer={actions?.onOpenReviewComposer}
                  onOpenAllReviews={actions?.onOpenAllReviews}
                />
              ) : null}
            </Box>
          ) : null}

          {hasPracticalInfo || visibility.location ? (
            <Box
              data-testid="gloss-lab-practical-grid"
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                alignItems: 'start',
                gap: { xs: 2, md: 3 },
                mt: { xs: 3, md: 5 },
                '& [data-testid="salon-section-opening_hours"]': { mt: 0 },
                '& [data-testid="salon-section-contact_location"]': { mt: 0 },
              }}
            >
              {hasPracticalInfo ? (
                <SalonTemplateOpeningHours
                  data={{ hours: openingHours, closures }}
                  variant="compact"
                  visible={visibility.opening_hours}
                  markClosuresAsBlock={false}
                />
              ) : null}
              <SalonTemplateContactLocation
                data={identity}
                variant="compact"
                visible={visibility.location}
                mode={mode}
              />
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

export default GlossLabTemplate;
